import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'

import { ApHubTypeEnum } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { useGetOrganisationHierarchyInfoModal } from '@/modules/admin-center/organisation-hierarchy/hooks/useGetOrganisationHierarchyInfoModal'
import Button from '@/modules/shared/components/button/Button'
import { Fallback } from '@/modules/shared/components/fallback/Fallback'
import { Modal } from '@/modules/shared/components/modal/ModalComponent'
import { TableRowSingle } from '@/modules/shared/components/page-header-details-table/TableRowSingle'
import { PPNewDetailLink } from '@/modules/shared/components/ppnew-detail-link/PPNewDetailLink'
import { BooleanItem } from '@/modules/shared/components/table/table-column-items/shared/BooleanItem'

interface OrganisationHierarchyInfoModalProps {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
  organisationId: number
}

export function OrganisationHierarchyInfoModal({
  showModal,
  setShowModal,
  organisationId,
}: OrganisationHierarchyInfoModalProps) {
  const { t } = useTranslation()
  const { organisation, loadingStates, error } = useGetOrganisationHierarchyInfoModal({
    organisationId,
    skip: !showModal,
  })
  const { id, name, legalEntity, departmentCode, isVerified } = organisation || {}
  const purchaser = organisation?.__typename === 'Purchaser' ? organisation : undefined

  const onCloseModal = () => {
    setShowModal(false)
  }

  return (
    <>
      <Modal showModal={showModal} onCloseModal={onCloseModal} loading={loadingStates.loading} error={error}>
        <Modal.Panel className="flex max-h-[80vh] w-full flex-col overflow-hidden rounded-md bg-white text-sm shadow-xl transition-all md:max-w-[56.25rem]">
          <Modal.Title
            title={t('general.organisationUnitInformation', 'Organisation Unit Information')}
            onCloseModal={onCloseModal}
          />
          <Modal.Body>
            <div className="w-full divide-y divide-gray-200 overflow-auto rounded-md border border-gray-300">
              <TableRowSingle
                titleText={t('general.organisationUnit', 'Organisation Unit')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.organisationUnitTooltip',
                  'The name of the Organisation Unit.'
                )}
                testId="organisationUnit"
                value={name}
              />
              <TableRowSingle
                titleText={t('general.organisationUnitId', 'Organisation Unit ID')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.organisationUnitIdTooltip',
                  'The unique identifier for the Organisation Unit in PurchasePlus.'
                )}
                testId="organisationUnitId"
                value={id}
              />
              <TableRowSingle
                titleText={t('general.legalEntity', 'Legal Entity')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.legalEntityTooltip',
                  'Indicates if the Organisation is a Legal Entity or not. In PurchasePlus, a Legal Entity is treated differently to a Non-Legal Entity.'
                )}
                testId="legalEntity"
                value={<BooleanItem value={!!legalEntity} />}
              />
              <TableRowSingle
                titleText={t('general.isVerified', 'Is Verified')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.isVerifiedTooltip',
                  'Indicates that the Organisation is a formal user of PurchasePlus.'
                )}
                testId="isVerified"
                value={<BooleanItem value={!!isVerified} />}
              />
              <TableRowSingle
                titleText={t('general.costCenterCode', 'Cost Center Code')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.costCenterCodeTooltip',
                  'The Cost Center Code that applies to the Organisation Unit. This can be useful to help identify expenses that apply to this Organisation.'
                )}
                testId="costCentreCode"
                value={departmentCode}
              />
              <TableRowSingle
                titleText={t('general.defaultAccount', 'Default Account')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.defaultAccountCodeTooltip',
                  'The Account that will be selected by default when a Document is created against this Organisation Unit.'
                )}
                testId="defaultAccountCode"
                value={
                  <Fallback condition={!!purchaser?.defaultAccount}>
                    <PPNewDetailLink type="account-code" id={String(purchaser?.defaultAccount?.id)}>
                      {purchaser?.defaultAccount?.codedName}
                    </PPNewDetailLink>
                  </Fallback>
                }
              />
              <TableRowSingle
                titleText={t('general.PaperlessEmailAddress', 'Paperless Email Address')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.paperlessEmailAddressTooltip',
                  'The primary email address that can be used to send PDF Invoices or Credit Notes to this Organisation. The PDF attachments will be checked, scanned and created in PurchasePlus as a new Invoice or Credit Note.'
                )}
                testId="paperlessEmailAddress"
                value={purchaser?.paperlessInvoicingIdentityEmail}
              />
              <TableRowSingle
                titleText={t('general.requisitionApprovalWorkflow', 'Requisition Approval Workflow')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.requisitionApprovalWorkflowTooltip',
                  'The Approval Workflow that Requisitions raised against this Organisation Unit will be placed into.'
                )}
                testId="requisitionApprovalWorkflow"
                value={
                  <Fallback condition={!!purchaser?.requisitionWorkflow}>
                    {
                      <PPNewDetailLink type="approval-workflow" id={String(purchaser?.requisitionWorkflow?.id)}>
                        {purchaser?.requisitionWorkflow?.name}
                      </PPNewDetailLink>
                    }
                  </Fallback>
                }
              />
              <TableRowSingle
                titleText={t('general.invoiceApprovalWorkflow', 'Invoice Approval Workflow')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.invoiceApprovalWorkflowTooltip',
                  'The Approval Workflow that Invoices raised against this Organisation Unit will be placed into.'
                )}
                testId="invoiceApprovalWorkflow"
                value={
                  <Fallback condition={!!purchaser?.invoiceWorkflow}>
                    {
                      <PPNewDetailLink type="approval-workflow" id={String(purchaser?.invoiceWorkflow?.id)}>
                        {purchaser?.invoiceWorkflow?.name}
                      </PPNewDetailLink>
                    }
                  </Fallback>
                }
              />
              <TableRowSingle
                titleText={t('general.creditNoteApprovalWorkflow', 'Credit Note Approval Workflow')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.creditNoteApprovalWorkflowTooltip',
                  'The Approval Workflow that Credit Notes raised against this Organisation Unit will be placed into.'
                )}
                testId="creditNoteApprovalWorkflow"
                value={
                  <Fallback condition={!!purchaser?.creditNoteWorkflow}>
                    {
                      <PPNewDetailLink type="approval-workflow" id={String(purchaser?.creditNoteWorkflow?.id)}>
                        {purchaser?.creditNoteWorkflow?.name}
                      </PPNewDetailLink>
                    }
                  </Fallback>
                }
              />
              <TableRowSingle
                titleText={t('general.apSystemType', 'AP System Type')}
                tooltipText={t(
                  'organisationHierarchy.organisationDetails.apSystemTypeTooltip',
                  'The AP System type that users at this Organisation will experience. Type A should be used if the Organisation exports Invoices and Credit Notes one at a time, to a preset electronic destination. Type B should be used if the Organisation manually creates Export Files via the Export Center, and downloads them or sends them to an electronic destination at their discretion.'
                )}
                testId="apSystemType"
                value={
                  purchaser?.featureFlags?.apHubType === ApHubTypeEnum.TypeAAutomated
                    ? t('general.typeA', 'Type A')
                    : t('general.typeB', 'Type B')
                }
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="flex flex-col-reverse items-center justify-end gap-2 md:flex-row">
            <Button
              type="button"
              className="h-11 w-full rounded-md bg-gray-200 px-5 text-sm md:w-fit"
              onClick={onCloseModal}
            >
              {t('general.close', 'Close')}
            </Button>
          </Modal.Footer>
        </Modal.Panel>
      </Modal>
    </>
  )
}
